import { format } from "date-fns";
import { Bot, FileJson, MoreHorizontal, Plus } from "lucide-react";
import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { featureTemplateService } from "@/services/featureTemplateService";
import { FeatureTemplateWithDetails } from "@/types/featureTemplate";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NoData } from "@/components/ui/no-data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FeatureTemplateForm } from "./FeatureTemplateForm";

interface FeatureTemplatesListProps {
  organizationId: string;
  isCreateDialogOpen?: boolean;
  setIsCreateDialogOpen?: Dispatch<SetStateAction<boolean>>;
}

export function FeatureTemplatesList({
  organizationId,
  isCreateDialogOpen: externalIsCreateDialogOpen,
  setIsCreateDialogOpen: externalSetIsCreateDialogOpen,
}: FeatureTemplatesListProps) {
  const [templates, setTemplates] = useState<FeatureTemplateWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalIsCreateDialogOpen, setInternalIsCreateDialogOpen] = useState(false);
  
  const isCreateDialogOpen = externalIsCreateDialogOpen ?? internalIsCreateDialogOpen;
  const setIsCreateDialogOpen = externalSetIsCreateDialogOpen ?? setInternalIsCreateDialogOpen;
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await featureTemplateService.getFeatureTemplates(
        organizationId
      );
      setTemplates(data);
    } catch (error) {
      console.error("Error fetching feature templates:", error);
      toast({
        title: "Error",
        description: "Failed to load feature templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [organizationId]);

  const handleDelete = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      await featureTemplateService.deleteFeatureTemplate(templateId);
      toast({
        title: "Template deleted",
        description: "The feature template has been deleted successfully",
      });
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting feature template:", error);
      toast({
        title: "Error",
        description: "Failed to delete feature template",
        variant: "destructive",
      });
    }
  };

  const viewTemplateDetails = (templateId: string) => {
    navigate(`/feature-templates/${templateId}`);
  };

  const renderLimitBadge = (
    value: number | undefined | null,
    label: string
  ) => {
    if (value === undefined || value === null) return null;
    return (
      <Badge variant="secondary" className="mr-2">
        {label}: {value}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-6 bg-background text-foreground">
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner text="Loading templates..." />
          </div>
        ) : templates.length === 0 ? (
          <NoData
            icon={<FileJson className="h-12 w-12 text-muted-foreground/50" />}
            title="No feature templates found"
            description="Get started by creating your first feature template."
            actionButton={
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                variant="primaryOutline"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Template
              </Button>
            }
          />
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Schema Template</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow
                    key={template.id}
                    className="cursor-pointer hover:bg-accent/50"
                    onClick={() => viewTemplateDetails(template.id)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <span className="truncate max-w-[200px]">
                          {template.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-muted-foreground" />
                        <span>{template.assistant.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileJson className="h-4 w-4 text-muted-foreground" />
                        <span>{template.schema_template.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex flex-wrap gap-1">
                              {renderLimitBadge(template.max_instances, "Instances")}
                              {renderLimitBadge(
                                template.max_leads_per_instance,
                                "Leads"
                              )}
                              {renderLimitBadge(
                                template.max_call_minutes_per_instance,
                                "Minutes"
                              )}
                              {renderLimitBadge(
                                template.max_reports_per_instance,
                                "Reports"
                              )}
                              {!template.max_instances &&
                                !template.max_leads_per_instance &&
                                !template.max_call_minutes_per_instance &&
                                !template.max_reports_per_instance && (
                                  <Badge variant="outline">No limits</Badge>
                                )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1">
                              <p>
                                Maximum Instances:{" "}
                                {template.max_instances || "No limit"}
                              </p>
                              <p>
                                Maximum Leads per Instance:{" "}
                                {template.max_leads_per_instance || "No limit"}
                              </p>
                              <p>
                                Maximum Call Minutes per Instance:{" "}
                                {template.max_call_minutes_per_instance || "No limit"}
                              </p>
                              <p>
                                Maximum Reports per Instance:{" "}
                                {template.max_reports_per_instance || "No limit"}
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(template.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          asChild
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              viewTemplateDetails(template.id);
                            }}
                          >
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(template.id);
                            }}
                            className="text-danger"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Feature Template</DialogTitle>
            </DialogHeader>
            <FeatureTemplateForm
              organizationId={organizationId}
              onComplete={() => {
                setIsCreateDialogOpen(false);
                fetchTemplates();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
